'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useId, useRef, useState } from 'react';
import {
  Binoculars,
  Briefcase,
  Grid3x3,
  LayoutDashboard,
  Menu,
  Newspaper,
  Package,
  Radar,
  Star,
  X,
} from 'lucide-react';
import { BASTION_ACCENT } from '@/lib/bastion-accent';
import { PRODUCT_OVERVIEW_PATH } from '@/lib/hub/public-nav-links';

const APPS = [
  { href: '/hub', label: 'Hub', icon: LayoutDashboard },
  { href: '/radar', label: 'Radar', icon: Radar },
  { href: '/portfolios', label: 'Portfolios', icon: Briefcase },
  { href: '/heatmap', label: 'Heatmap', icon: Grid3x3 },
  { href: '/watchlist', label: 'Watchlist', icon: Star },
  { href: '/headlines', label: 'Headlines', icon: Newspaper },
  { href: '/watchtower', label: 'Watchtower', icon: Binoculars },
  { href: PRODUCT_OVERVIEW_PATH, label: 'Product', icon: Package },
] as const;

const FONT = 'var(--font-bastion)';

function isAppActive(pathname: string | null, href: string): boolean {
  if (!pathname) return false;
  if (href === '/hub') return pathname === '/hub';
  if (href === '/') return pathname === '/';
  if (href === PRODUCT_OVERVIEW_PATH) return pathname === PRODUCT_OVERVIEW_PATH;
  return pathname === href || pathname.startsWith(`${href}/`);
}

export default function MobileAppsMenu() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="md:hidden shrink-0" style={{ position: 'relative', zIndex: open ? 57 : undefined }}>
      <button
        ref={buttonRef}
        type="button"
        className="topnav-logo-btn flex items-center justify-center cursor-pointer"
        aria-label={open ? 'Close apps menu' : 'Open apps menu'}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        style={{
          position: 'relative',
          zIndex: open ? 58 : undefined,
          width: 36,
          height: 36,
          borderRadius: '50%',
          border: open
            ? '1px solid rgba(220,226,236,0.35)'
            : '1px solid rgba(255,255,255,0.08)',
          background: '#1a1a1a',
          transition: 'all 150ms ease',
          color: open ? BASTION_ACCENT : 'rgba(255,255,255,0.85)',
          padding: 0,
        }}
      >
        {open ? <X size={18} strokeWidth={2} /> : <Menu size={18} strokeWidth={2} />}
      </button>

      {open ? (
        <>
          <button
            type="button"
            aria-label="Dismiss apps menu"
            onClick={() => {
              setOpen(false);
              buttonRef.current?.focus();
            }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 55,
              border: 'none',
              background: 'rgba(0,0,0,0.55)',
              padding: 0,
              cursor: 'default',
            }}
          />
          <div
            id={panelId}
            role="dialog"
            aria-modal="true"
            aria-label="Bastion apps"
            style={{
              position: 'fixed',
              top: 'calc(52px + env(safe-area-inset-top, 0px))',
              left: 0,
              right: 0,
              zIndex: 56,
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                pointerEvents: 'auto',
                margin: '0 12px',
                padding: '14px 14px 16px',
                borderRadius: 16,
                background: '#1e1e1e',
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 16px 40px rgba(0,0,0,0.55)',
                fontFamily: FONT,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 14,
                  padding: '0 2px',
                }}
              >
                <img src="/iconb-mark.png" alt="" width={17} height={17} style={{ display: 'block' }} />
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.55)',
                  }}
                >
                  Menu
                </span>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: 8,
                }}
              >
                {APPS.map((app) => {
                  const active = isAppActive(pathname, app.href);
                  const Icon = app.icon;
                  return (
                    <Link
                      key={app.href}
                      href={app.href}
                      onClick={() => setOpen(false)}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 7,
                        minHeight: 84,
                        padding: '12px 8px',
                        borderRadius: 12,
                        textDecoration: 'none',
                        background: active
                          ? 'rgba(220,226,236,0.12)'
                          : 'rgba(255,255,255,0.04)',
                        border: active
                          ? '1px solid rgba(220,226,236,0.35)'
                          : '1px solid rgba(255,255,255,0.06)',
                        color: active ? BASTION_ACCENT : 'rgba(255,255,255,0.78)',
                        transition: 'background 0.15s ease, border-color 0.15s ease',
                      }}
                    >
                      <Icon size={22} strokeWidth={active ? 2 : 1.6} />
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: active ? 650 : 500,
                          lineHeight: 1.15,
                          textAlign: 'center',
                          letterSpacing: '0.01em',
                        }}
                      >
                        {app.label}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
